import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import { useMemo } from 'react'

export interface ChatContext {
  scope: string
  entityType?: string
  entityId?: string
  entityName?: string
}

export function useChatContext(): ChatContext {
  const location = useLocation()
  const params = useParams()
  const [searchParams] = useSearchParams()

  return useMemo(() => {
    const path = location.pathname

    // KG Browser — read scope from query params
    if (path === '/kg') {
      const kgScope = searchParams.get('scope') || 'portfolio'
      // Support both entityIds (comma-separated) and legacy entityId
      const kgEntityIds = searchParams.get('entityIds')?.split(',').filter(Boolean) || []
      const kgEntityId = kgEntityIds[0] || searchParams.get('entityId') || undefined
      const kgEntityName = searchParams.get('entityName') || undefined
      const scopeMap: Record<string, string> = {
        portfolio: 'kg-portfolio',
        customer: 'kg-customer',
        territory: 'kg-territory',
        ae: 'kg-ae',
      }
      return {
        scope: scopeMap[kgScope] || 'kg-portfolio',
        entityType: kgScope === 'portfolio' ? undefined : kgScope,
        entityId: kgEntityId,
        entityName: kgEntityName,
      }
    }

    if (path.match(/^\/leads\/[^/]+$/)) {
      return { scope: 'lead', entityType: 'lead', entityId: params.leadId }
    }
    if (path === '/leads') {
      return { scope: 'leads' }
    }
    if (path.match(/^\/csp\/[^/]+$/)) {
      return { scope: 'csp-customer', entityType: 'customer', entityId: params.customerId }
    }
    if (path === '/csp') {
      return { scope: 'csp-customers' }
    }
    if (path.match(/^\/ae\/territory\/[^/]+$/)) {
      return { scope: 'territory-analysis', entityType: 'territory', entityId: params.territoryId }
    }
    if (path.match(/^\/ae\/[^/]+$/) && params.aeId) {
      return { scope: 'ae-portfolio', entityType: 'ae', entityId: params.aeId }
    }
    if (path === '/ae') {
      return { scope: 'ae-management' }
    }
    if (path === '/it-budgets') {
      return { scope: 'it-budgets' }
    }
    if (path === '/gitlab-features') {
      return { scope: 'gitlab-features' }
    }
    if (path === '/settings') {
      return { scope: 'settings' }
    }

    return { scope: 'app' }
  }, [location.pathname, params.leadId, params.customerId, params.aeId, params.territoryId, searchParams])
}
